import type { RootState } from '@/state';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';


/**
 * A premium top loading bar that responds to ALL RTK Query requests globally.
 * Features a vibrant gradient and simulated progress for a smooth feel.
 */
export const TopLoadingBar = () => {
  const [progress, setProgress] = useState(0);
  const [isVisible, setIsVisible] = useState(false);

  // Selector to check ANY RTK Query API for pending requests or mutations
  const isAnyLoading = useSelector((state: RootState) => {
    return Object.values(state).some((slice: any) => {
      // Check for queries
      if (slice && typeof slice === 'object' && slice.queries) {
        return Object.values(slice.queries).some(
          (q: any) => q?.status === 'pending'
        );
      }
      // Check for mutations
      if (slice && typeof slice === 'object' && slice.mutations) {
        return Object.values(slice.mutations).some(
          (m: any) => m?.status === 'pending'
        );
      }
      return false;
    });
  });

  useEffect(() => {
    let interval: NodeJS.Timeout;

    if (isAnyLoading) {
      setIsVisible(true);
      // Start at 10%
      setProgress((prev) => (prev < 10 ? 10 : prev));

      // Simulated progress animation
      interval = setInterval(() => {
        setProgress((prev) => {
          if (prev < 30) return prev + 5;
          if (prev < 70) return prev + 2;
          if (prev < 90) return prev + 0.5;
          return prev;
        });
      }, 300);
    } else {
      // When loading stops, jump to 100%
      setProgress(100);

      // Fade out after a short delay
      const timeout = setTimeout(() => {
        setIsVisible(false);
        setProgress(0);
      }, 400);

      return () => {
        clearTimeout(timeout);
        clearInterval(interval);
      };
    }

    return () => clearInterval(interval);
  }, [isAnyLoading]);

  if (!isVisible && progress === 0) return null;

  return (
    <div
      className={`fixed top-0 left-0 w-full z-[9999] h-[3px] pointer-events-none transition-opacity duration-300 ${isVisible ? 'opacity-100' : 'opacity-0'
        }`}
    >
      <div 
        className="h-full bg-primary shadow-[0_0_10px_var(--primary)] shadow-primary/30 transition-all duration-300 ease-out"
        style={{ width: `${progress}%` }}
      />
    </div>
  );
};
